import { useEffect, useState } from 'react';
import { fetchMyInvitationsAsync } from '../../../invitations/services/invitationSvc';
import { usePendingInvitationsStore } from '@features/user/invitations/store/pendingInvitationsStore';

export type MyInvitationsParams = {
  query?: string;
  status?: string;
  page?: number;
  perPage?: number;
};

/**
 * Custom React hook to fetch and manage the user's invitations with pagination and filtering support.
 *
 * @param params - Parameters for fetching invitations.
 * @param params.query - Optional search query string to filter invitations.
 * @param params.status - Optional status filter for invitations.
 * @param params.page - Optional current page number for pagination.
 * @param params.perPage - Optional number of invitations per page.
 *
 * @returns Invitation list state plus `loading` and `reload`.
 *
 * @example
 * const { data, loading, reload } = useMyInvitations({ query: 'john', status: 'pending', page: 1, perPage: 10 });
 */
export function useMyInvitations(params: MyInvitationsParams) {
    const [current_page, setCurrentPage] = useState(params.page || 1);
    const [data, setData] = useState<any[]>([]);
    const [first_page_url, setFirstPageUrl] = useState<string | undefined>(undefined);
    const [from, setFrom] = useState<number | undefined>(undefined);
    const [last_page, setLastPage] = useState<number | undefined>(undefined);
    const [last_page_url, setLastPageUrl] = useState<string | undefined>(undefined);
    const [links, setLinks] = useState<{ url: string | null; label: string; active: boolean }[] | undefined>(undefined);
    const [next_page_url, setNextPageUrl] = useState<string | null | undefined>(undefined);
    const [path, setPath] = useState<string | undefined>(undefined);
    const [per_page, setPerPage] = useState<number | undefined>(undefined);
    const [prev_page_url, setPrevPageUrl] = useState<string | null | undefined>(undefined);
    const [to, setTo] = useState<number | undefined>(undefined);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);

    const reload = async () => {
        setLoading(true);
        try {
            const response = await fetchMyInvitationsAsync(params);

            if (response.isSuccess()) {
                const payload = response.getData();
                setCurrentPage(payload?.current_page || 1);
                setData(payload?.data || []);
                setFirstPageUrl(payload?.first_page_url);
                setFrom(payload?.from);
                setLastPage(payload?.last_page);
                setLastPageUrl(payload?.last_page_url);
                setLinks(payload?.links);
                setNextPageUrl(payload?.next_page_url);
                setPath(payload?.path);
                setPerPage(payload?.per_page);
                setPrevPageUrl(payload?.prev_page_url);
                setTo(payload?.to);
                const nextTotal = payload?.total || 0;
                setTotal(nextTotal);

                // Keep chrome badge/banner aligned for the canonical unfiltered pending list.
                if (!params.query && !params.status) {
                    usePendingInvitationsStore.getState().setTotal(nextTotal);
                }
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, [params.query, params.status, params.page, params.perPage]);

    return { current_page, data, first_page_url, from, last_page, last_page_url, links, next_page_url, path, per_page, prev_page_url, to, total, loading, reload };
}
